import { useEffect, useRef, useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { ArrowLeft, Camera, FileUp, Loader2, X } from "lucide-react"
import type { AppShell } from "@/App"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { createReceipt, uploadFile } from "./api"
import { ActionBar, ActionBarSpacer } from "./ui"

const MAX_FILES = 10
const MAX_BYTES = 20 * 1024 * 1024

/**
 * Nova receção: tirar foto(s) ao papel ou escolher o PDF. Uma fatura com várias
 * páginas leva várias fotos (a Solarshop põe o bloco de séries noutra página):
 * vão todas juntas como um documento.
 */
export function NewReceipt({ shell }: { shell: AppShell }) {
  const navigate = useNavigate()
  const camera = useRef<HTMLInputElement>(null)
  const picker = useRef<HTMLInputElement>(null)
  // Vindo da lista no telemóvel («Tirar foto» / «Escolher ficheiro»): as primeiras páginas.
  const initial = (useLocation().state ?? null) as { files?: File[]; fromCamera?: boolean } | null
  const [files, setFiles] = useState<File[]>(() => (initial?.files ?? []).filter((f) => f instanceof File))
  // «Foto» se alguma página veio da câmara; senão «Ficheiro».
  const [fromCamera, setFromCamera] = useState(initial?.fromCamera === true)
  const [previews, setPreviews] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const urls = files.map((f) => (f.type.startsWith("image/") ? URL.createObjectURL(f) : ""))
    setPreviews(urls)
    return () => urls.forEach((u) => u && URL.revokeObjectURL(u))
  }, [files])

  const add = (list: FileList | null, camera = false) => {
    if (!list) return
    const next = [...files, ...Array.from(list)]
    const tooBig = next.find((f) => f.size > MAX_BYTES)
    if (tooBig) return setError(`${tooBig.name} tem mais de 20 MB.`)
    if (next.length > MAX_FILES) return setError(`No máximo ${MAX_FILES} ficheiros por documento.`)
    setError(null)
    setFiles(next)
    if (camera) setFromCamera(true)
  }

  const send = async () => {
    setBusy(true)
    setError(null)
    try {
      const keys: string[] = []
      for (const f of files) keys.push(await uploadFile(shell, f))
      const receipt = await createReceipt(shell, keys, fromCamera ? "foto" : "ficheiro")
      navigate(`/armazem/${receipt.id}`, { replace: true })
    } catch (e) {
      setError((e as Error).message)
      setBusy(false)
    }
  }

  return (
    <div className="mx-auto max-w-xl space-y-4">
      <Link to="/armazem" className="inline-flex h-12 items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground sm:h-8">
        <ArrowLeft className="h-4 w-4" /> Receções
      </Link>
      <h1 className="text-xl font-semibold">Nova receção</h1>
      <Card>
        <CardContent className="space-y-4 p-4">
          <p className="text-sm text-muted-foreground">
            Fotografe a fatura ou a guia do fornecedor, de frente e com luz, ou escolha o PDF. Se tiver várias páginas,
            junte uma foto por página.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="h-[76px] flex-col gap-1 text-base" onClick={() => camera.current?.click()} disabled={busy}>
              <Camera className="h-6 w-6" /> {files.length ? "Mais uma foto" : "Tirar foto"}
            </Button>
            <Button variant="outline" className="h-[76px] flex-col gap-1 text-base" onClick={() => picker.current?.click()} disabled={busy}>
              <FileUp className="h-6 w-6" /> Escolher ficheiro
            </Button>
          </div>
          <input
            ref={camera}
            type="file"
            accept="image/*,application/pdf"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              add(e.target.files, true)
              e.target.value = ""
            }}
          />
          <input
            ref={picker}
            type="file"
            accept="image/*,application/pdf"
            multiple
            className="hidden"
            onChange={(e) => {
              add(e.target.files)
              e.target.value = ""
            }}
          />

          {files.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {files.map((f, i) => (
                <div key={`${f.name}-${i}`} className="relative overflow-hidden rounded border bg-muted">
                  {previews[i] ? (
                    <img src={previews[i]} alt={`Página ${i + 1}`} className="h-28 w-full object-cover" />
                  ) : (
                    <div className="flex h-28 items-center justify-center p-2 text-center text-xs">{f.name}</div>
                  )}
                  <span className="absolute left-1 top-1 rounded bg-background/80 px-1.5 text-sm">{i + 1}</span>
                  {!busy && (
                    <button
                      type="button"
                      className="absolute right-0 top-0 flex h-11 w-11 items-center justify-center"
                      onClick={() => setFiles(files.filter((_, k) => k !== i))}
                      aria-label="Tirar"
                    >
                      <span className="rounded-full bg-background/90 p-1">
                        <X className="h-4 w-4" />
                      </span>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
        </CardContent>
      </Card>
      <ActionBarSpacer />
      <ActionBar>
        <Button className="h-14 flex-1 text-base font-semibold" disabled={files.length === 0 || busy} onClick={send}>
          {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : null}
          {busy ? "A enviar…" : files.length > 1 ? `Enviar documento (${files.length} páginas)` : "Enviar documento"}
        </Button>
      </ActionBar>
    </div>
  )
}
